import { useState, useMemo, useEffect } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import { toast } from 'react-toastify';

import { createProjectMeetingNote, updateProjectMeetingNotes, deleteProjectMeetingNotes, getProjectMeetingNoteTypes } from "../../api/meetingNotes";
import { useProjectMeetingNotesStore, useProjectMeetingNotesPage } from "../../state/projects";
import { MeetingNotesDocumentAttachment } from './MeetingNotesDocumentAttachment';
import { AddMeetingNoteModal } from './AddMeetingNoteModal';
import { MeetingTypesManager } from './MeetingTypesManager';
import { AttendeeSelectionModal } from './AttendeeSelectionModal';

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCalendarAlt,
  FaFileAlt,
  FaTrash
} from 'react-icons/fa';

import { 
  getTableHeaderStyle, 
  getFormLabelStyle, 
  getMultilineFieldStyle,
  getFormSelectStyle, 
  getFormSelectContainerStyle,
  DesignSystem
} from '../../styles/designSystem';

interface TabMeetingNotesProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  highlightedItemId?: string | null;
}

// Meeting Note Row Component
const MeetingNoteRow = ({ 
  meetingNote, 
  projectData, 
  onSubmit, 
  onDelete,
  meetingTypes,
  onMeetingNoteUpdate,
  isNew = false,
  isHighlighted = false
}: { 
  meetingNote: ProjectMeetingNote, 
  projectData: ProjectInterface, 
  onSubmit: Function,
  onDelete: (meetingNoteId: string) => void,
  meetingTypes: ProjectMeetingNoteTypesInterface[],
  onMeetingNoteUpdate?: () => Promise<void>,
  isNew?: boolean,
  isHighlighted?: boolean
}) => {
  // Normalize meeting note data - ensure customers/vendors are arrays of UUIDs
  const normalizeAttendees = (meetingNote: ProjectMeetingNote) => {
    const normalized = { ...meetingNote };
    
    // Normalize customers - convert objects to UUID strings
    if (normalized.customers) {
      normalized.customers = normalized.customers.map((customer: any) => {
        if (typeof customer === 'string') return customer;
        return customer.id || customer.user_id || customer;
      });
    }
    
    // Normalize vendors - convert objects to UUID strings  
    if (normalized.vendors) {
      normalized.vendors = normalized.vendors.map((vendor: any) => {
        if (typeof vendor === 'string') return vendor;
        return vendor.id || vendor.user_id || vendor;
      });
    }
    
    return normalized;
  };

  const [form, setForm] = useState(normalizeAttendees(meetingNote));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);

  useEffect(() => {
    setForm(normalizeAttendees(meetingNote));
  }, [meetingNote]);

  // Function to save changes
  const saveChanges = async (updatedForm: any) => {
    setIsSaving(true);
    
    // Normalize data before saving - ensure customers/vendors are UUID strings
    const normalizedData = {
      ...updatedForm,
      customers: updatedForm.customers?.map((customer: any) => {
        if (typeof customer === 'string') return customer;
        return customer.id || customer.user_id || customer;
      }) || [],
      vendors: updatedForm.vendors?.map((vendor: any) => {
        if (typeof vendor === 'string') return vendor;
        return vendor.id || vendor.user_id || vendor;
      }) || []
    };
    
    console.log('Meeting Note: Saving changes:', normalizedData);
    try {
      await onSubmit(normalizedData);
      console.log('Meeting Note: Successfully saved changes');
    } catch (error) {
      console.error('Error saving meeting note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    const updatedForm = { ...form, [field]: value };
    setForm(updatedForm);
    
    // Auto-save for certain fields immediately
    if (['date', 'meeting_type_id', 'customers', 'vendors'].includes(field)) {
      saveChanges(updatedForm);
    }
  };

  // Function to handle blur for text fields
  const handleFieldBlur = (field: string, value: any) => {
    const updatedForm = { 
      ...form, 
      [field]: value 
    };
    
    setForm(updatedForm);
    saveChanges(updatedForm);
  };

  const handleMeetingNoteUpdate = async () => {
    // Reload meeting notes to show updated document
    if (onMeetingNoteUpdate) {
      await onMeetingNoteUpdate();
    }
  };

  // Get attendee names for display
  const getAttendeeName = (userId: string | any) => {
    // Handle both string IDs and user objects
    let actualUserId = userId;
    if (typeof userId === 'object' && userId !== null) {
      actualUserId = userId.id || userId.user_id;
    }
    
    const user = projectData?.users?.find(u => (u.id || u.user_id) === actualUserId);
    if (!user) return 'Unknown User';
    return `${user.user?.first_name || user.first_name} ${user.user?.last_name || user.last_name}`;
  };

  const handleCustomerSelection = (selectedUsers: string[]) => {
    handleFieldChange('customers', selectedUsers);
  };

  const handleVendorSelection = (selectedUsers: string[]) => {
    handleFieldChange('vendors', selectedUsers);
  };

  // Get meeting type name
  const getMeetingTypeName = (typeId: string) => {
    const type = meetingTypes.find(t => t.id === typeId);
    return type?.name || 'No Type';
  };

  return (
    <div className={`group hover:bg-gray-50/50 transition-all duration-200 ${
      isNew ? 'bg-blue-50/30' : ''
    } ${
      isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
    }`}>
      {/* Main Row */}
      <div className="flex items-start px-6 py-4 relative" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-gray-100 transition-colors relative z-10"
        >
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Date */}
        <div className="mr-6 w-32 relative z-10 flex items-center pt-1">
          <div className="relative mr-2">
            {/* Rotating Ring around calendar icon - when saving any field */}
            {isSaving && (
              <div className="absolute inset-0 w-5 h-5 -ml-1 -mt-1 rounded-full border-2 border-transparent animate-spin border-t-indigo-600/60 border-r-indigo-600/60" style={{ animation: 'spin 1s linear infinite' }} />
            )}
            <FaCalendarAlt className="w-3 h-3 text-gray-400" />
          </div>
          <span className="text-sm text-gray-700">
            {form.date ? (() => {
              // Parse date without timezone conversion to avoid shifting
              const dateStr = form.date.includes('T') ? form.date.split('T')[0] : form.date;
              const [year, month, day] = dateStr.split('-');
              const formattedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
              console.log('Displaying date:', form.date, '->', formattedDate);
              return formattedDate;
            })() : 'No Date'}
          </span>
        </div>

        {/* Meeting Type */}
        <div className="mr-6 w-40 relative z-10 pt-1">
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md">
            {getMeetingTypeName(form.meeting_type_id)}
          </span>
        </div>

        {/* Notes Preview */}
        <div className="flex-1 min-w-0 relative z-10 flex items-start">
          <textarea
            value={form.notes || ''}
            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
            onBlur={(e) => handleFieldBlur('notes', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-gray-400 resize-none"
            style={getMultilineFieldStyle()}
            placeholder="Meeting notes..."
            rows={5}
          />
        </div>

        {/* Customer Attendee Preview */}
        <div className="mx-6 w-40 relative z-10 flex items-center space-x-1 pt-1">
          {form.customers && form.customers.length > 0 ? (
            <>
              <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-indigo-600">C</span>
              </div>
              <span className="text-xs text-gray-600 truncate">
                {form.customers.length === 1 
                  ? getAttendeeName(form.customers[0])
                  : `${form.customers.length} customers`
                }
              </span>
            </>
          ) : (
            <>
              <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-gray-400">C</span>
              </div>
              <span className="text-xs text-gray-400">No customers</span>
            </>
          )}
        </div>

        {/* Vendor Attendee Preview */}
        <div className="mx-6 w-40 relative z-10 flex items-center space-x-1 pt-1">
          {form.vendors && form.vendors.length > 0 ? (
            <>
              <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-indigo-600">V</span>
              </div>
              <span className="text-xs text-gray-600 truncate">
                {form.vendors.length === 1 
                  ? getAttendeeName(form.vendors[0])
                  : `${form.vendors.length} vendors`
                }
              </span>
            </>
          ) : (
            <>
              <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-gray-400">V</span>
              </div>
              <span className="text-xs text-gray-400">No vendors</span>
            </>
          )}
        </div>

        {/* Documents */}
        <div className="ml-6 w-32 relative z-10 pt-1">
          <div onClick={(e) => e.stopPropagation()}>
            <MeetingNotesDocumentAttachment 
              projectId={projectData?.id || ''} 
              recordId={meetingNote.id}
              documentUrl={form.document || undefined}
              fileName={form.file_name || undefined}
              onUpdate={handleMeetingNoteUpdate}
            />
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2" style={{ 
          backgroundColor: DesignSystem.colors.background.gray50,
          borderTop: `0.5px solid ${DesignSystem.colors.border.gray200}` 
        }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Meeting Notes and Details */}
            <div className="lg:col-span-2 space-y-4">
              {/* Meeting Notes */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Meeting Notes</label>
                <textarea
                  value={form.notes || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('notes', e.target.value)}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Add meeting notes, discussions, decisions, and action items..."
                  rows={6}
                  style={{ 
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: DesignSystem.typography.body.fontSize 
                  }}
                />
              </div>
            </div>

            {/* Meeting Details and Attendees */}
            <div className="space-y-4">
              
              {/* Meeting Date */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Meeting Date</label>
                <div style={getFormSelectContainerStyle()}>
                  <input
                    type="date"
                    value={(() => {
                      if (!form.date) return '';
                      // Extract date part without timezone conversion
                      const dateStr = form.date.includes('T') ? form.date.split('T')[0] : form.date;
                      console.log('Date display:', form.date, '->', dateStr);
                      return dateStr;
                    })()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        // Store as simple date string, let API handle timezone
                        const isoValue = value + 'T00:00:00.000Z';
                        console.log('Date change:', value, '->', isoValue);
                        handleFieldChange('date', isoValue);
                      } else {
                        handleFieldChange('date', '');
                      }
                    }}
                    className="w-full bg-transparent border-0 focus:outline-none"
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* Meeting Type */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Meeting Type</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.meeting_type_id || ''}
                    onChange={(e) => handleFieldChange('meeting_type_id', e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="">Select meeting type</option>
                    {meetingTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Customer Attendees */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Customer Attendees (up to 10)</label>
                
                {/* Selected Attendees Display */}
                {form.customers && form.customers.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {form.customers.map((userId) => (
                      <div
                        key={userId}
                        className="inline-flex items-center space-x-2 bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm"
                      >
                        <span>{getAttendeeName(userId)}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedCustomers = form.customers?.filter(id => id !== userId) || [];
                            handleFieldChange('customers', updatedCustomers);
                          }}
                          className="text-indigo-600 hover:text-indigo-800 ml-1"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={() => setShowCustomerModal(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-colors"
                >
                  {form.customers && form.customers.length > 0 
                    ? `Edit Customer Attendees (${form.customers.length}/10)`
                    : 'Add Customer Attendees'
                  }
                </button>
              </div>

              {/* Vendor Attendees */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Vendor Attendees (up to 10)</label>
                
                {/* Selected Attendees Display */}
                {form.vendors && form.vendors.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {form.vendors.map((userId) => (
                      <div
                        key={userId}
                        className="inline-flex items-center space-x-2 bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm"
                      >
                        <span>{getAttendeeName(userId)}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedVendors = form.vendors?.filter(id => id !== userId) || [];
                            handleFieldChange('vendors', updatedVendors);
                          }}
                          className="text-indigo-600 hover:text-indigo-800 ml-1"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={() => setShowVendorModal(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-colors"
                >
                  {form.vendors && form.vendors.length > 0 
                    ? `Edit Vendor Attendees (${form.vendors.length}/10)`
                    : 'Add Vendor Attendees'
                  }
                </button>
              </div>

              {/* Actions */}
              <div className="pt-4">
                <label className="block mb-2" style={getFormLabelStyle()}>Actions</label>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this meeting note?')) {
                      onDelete(meetingNote.id);
                    }
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Note</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendee Selection Modals */}
      <AttendeeSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        title="Select Customer Attendees"
        users={projectData?.users || []}
        selectedUsers={form.customers || []}
        maxUsers={10}
        onConfirm={handleCustomerSelection}
      />

      <AttendeeSelectionModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        title="Select Vendor Attendees"
        users={projectData?.users || []}
        selectedUsers={form.vendors || []}
        maxUsers={10}
        onConfirm={handleVendorSelection}
      />
    </div>
  );
};

export const TabMeetingNotes = (
  { token, projectData, projectId, onProjectUpdate, highlightedItemId }: TabMeetingNotesProps
) => {
  // Add cleanup flag to prevent DOM operations after unmount
  const [isUnmounted, setIsUnmounted] = useState(false);
  const [newRows, setNewRows] = useState<ProjectMeetingNote[]>([]);
  const [showAddMeetingNoteModal, setShowAddMeetingNoteModal] = useState(false);
  const [showMeetingTypesManager, setShowMeetingTypesManager] = useState(false);
  const [refreshMeetingTypes, setRefreshMeetingTypes] = useState(0);
  const [meetingTypes, setMeetingTypes] = useState<ProjectMeetingNoteTypesInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Load meeting notes
  const { projectMeetingNotes } = useProjectMeetingNotesStore();
  const projectMeetingNotesForProject = projectMeetingNotes[projectId || ''] || [];
  const getProjectMeetingNotes = useProjectMeetingNotesPage(projectId || '');

  useEffect(() => {
    if (projectId) {
      getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
    }
  }, [projectId]);

  // Load meeting types for filter
  useEffect(() => {
    const loadMeetingTypes = async () => {
      if (!token) return;
      
      try {
        const response = await getProjectMeetingNoteTypes(token);
        setMeetingTypes(response.results);
      } catch (error) {
        console.error('Failed to load meeting types:', error);
      }
    };

    loadMeetingTypes();
  }, [token, refreshMeetingTypes]); // Reload when types are updated

  // Filter meeting notes
  const filteredMeetingNotes = useMemo(() => {
    let filtered = [...projectMeetingNotesForProject, ...newRows];

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(note => 
        note.notes?.toLowerCase().includes(searchLower)
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(note => {
        if (!note.date) return false;
        const noteDate = new Date(note.date);
        
        switch (dateFilter) {
          case 'today':
            return noteDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return noteDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return noteDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(note => note.meeting_type_id === typeFilter);
    }

    return filtered;
  }, [projectMeetingNotesForProject, newRows, searchQuery, dateFilter, typeFilter]);

  const updateProjectMeetingNoteHandler = async (form: ProjectMeetingNote) => {
    if (token && projectId) {
      await updateProjectMeetingNotes(token, projectId, form.id, form);
      // Only reload meeting notes list, don't reload entire project for auto-save
      await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
      // Note: onProjectUpdate() removed from auto-save to prevent data loss
      // Only call it for major changes, not field-level saves
    }
  };

  const handleMeetingNoteDocumentUpdate = async () => {
    // Reload meeting notes to show updated document
    await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
  };

  const createProjectMeetingNoteHandler = async (idx: number, form: ProjectMeetingNote) => {
    if (token && projectId) {
      await createProjectMeetingNote(token, projectId, form);
      await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
      setNewRows(prev => prev.filter((_, index) => index !== idx));
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    }
  };

  const handleAddMeetingNote = async (itemData: any) => {
    try {
      if (token && projectId) {
        await createProjectMeetingNote(token, projectId, {
          ...itemData,
          id: crypto.randomUUID(),
          project_id: projectId,
        });
        await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
        toast.success('Meeting note added successfully');
      }
    } catch (error) {
      console.error('Failed to add meeting note:', error);
      toast.error('Failed to add meeting note');
      throw error;
    }
  };

  const deleteMeetingNoteHandler = async (meetingNoteId: string) => {
    if (!token || !projectId) return;

    try {
      await deleteProjectMeetingNotes(token, projectId, meetingNoteId);
      await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
      toast.success('Meeting note deleted successfully');
    } catch (error) {
      console.error('Error deleting meeting note:', error);
      toast.error('Failed to delete meeting note');
    }
  };

  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection for shadow effect
  useEffect(() => {
    if (isUnmounted) return;

    const handleScroll = () => {
      if (!isUnmounted) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUnmounted]);

  // Cleanup effect to prevent DOM operations after unmount
  useEffect(() => {
    return () => {
      setIsUnmounted(true);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-lg transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search meeting notes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {filteredMeetingNotes.length} of {projectMeetingNotesForProject.length} notes
                    </span>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span className="text-gray-500">
                      {filteredMeetingNotes.filter(note => note.date && new Date(note.date) >= new Date(Date.now() - 7*24*60*60*1000)).length} this week
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(dateFilter !== 'all' || typeFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                        {[dateFilter, typeFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* Manage Meeting Types */}
                  <button
                    onClick={() => setShowMeetingTypesManager(true)}
                    className="flex items-center space-x-2 bg-transparent text-gray-400 border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    <FaCalendarAlt className="w-3.5 h-3.5" />
                    <span>Manage Types</span>
                  </button>

                  {/* Add Meeting Note */}
                  <button
                    onClick={() => setShowAddMeetingNoteModal(true)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    <FaPlus className="w-3.5 h-3.5" />
                    <span>Add Meeting Note</span>
                  </button>
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #E5E7EB' }}>
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Types</option>
                        {meetingTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Active Filters & Clear */}
                  {(dateFilter !== 'all' || typeFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {dateFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Date: {dateFilter}
                            <button onClick={() => setDateFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                        {typeFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Type: {meetingTypes.find(t => t.id === typeFilter)?.name || typeFilter}
                            <button onClick={() => setTypeFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setDateFilter('all');
                          setTypeFilter('all');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="mr-6 w-32" role="columnheader">Date</div>
                <div className="mr-6 w-40" role="columnheader">Meeting Type</div>
                <div className="flex-1 min-w-0" role="columnheader">Notes</div>
                <div className="mx-6 w-40" role="columnheader">Customer</div>
                <div className="mx-6 w-40" role="columnheader">Vendor</div>
                <div className="ml-6 w-32" role="columnheader">Documents</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Notes List */}
      <div className="max-w-[1280px] mx-auto">
        {filteredMeetingNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <FaFileAlt className="w-12 h-12 mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No meeting notes found</h3>
            <p className="text-sm text-center max-w-md">
              {searchQuery || dateFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by adding your first meeting note'
              }
            </p>
          </div>
        ) : (
          <div>
            {/* New Rows */}
            {newRows.map((meetingNote, index) => (
              <MeetingNoteRow
                key={`new-${index}`}
                meetingNote={meetingNote}
                projectData={projectData}
                onSubmit={(form: ProjectMeetingNote) => createProjectMeetingNoteHandler(index, form)}
                onDelete={deleteMeetingNoteHandler}
                meetingTypes={meetingTypes}
                onMeetingNoteUpdate={handleMeetingNoteDocumentUpdate}
                isNew={true}
                isHighlighted={highlightedItemId === meetingNote.id}
              />
            ))}
            
            {/* Existing Rows */}
            {filteredMeetingNotes.map((meetingNote) => (
              <MeetingNoteRow
                key={meetingNote.id}
                meetingNote={meetingNote}
                projectData={projectData}
                onSubmit={updateProjectMeetingNoteHandler}
                onDelete={deleteMeetingNoteHandler}
                meetingTypes={meetingTypes}
                onMeetingNoteUpdate={handleMeetingNoteDocumentUpdate}
                isHighlighted={highlightedItemId === meetingNote.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Meeting Note Modal */}
      <AddMeetingNoteModal
        isOpen={showAddMeetingNoteModal}
        onClose={() => {
          setShowAddMeetingNoteModal(false);
          setRefreshMeetingTypes(prev => prev + 1); // Refresh types in case new ones were created
        }}
        onAddItem={handleAddMeetingNote}
        projectData={projectData}
        token={token}
      />

      {/* Meeting Types Manager Modal */}
      {showMeetingTypesManager && (
        <MeetingTypesManager
          token={token}
          onClose={() => {
            setShowMeetingTypesManager(false);
            setRefreshMeetingTypes(prev => prev + 1); // Trigger refresh
          }}
          isModal={true}
        />
      )}
    </div>
  );
}; 